import { Component, OnInit } from '@angular/core';
import Swal from 'sweetalert2';
import { SupplyService } from '../supply.service';

@Component({
  selector: 'app-supply-product',
  templateUrl: './supply-product.component.html',
  styleUrls: ['./supply-product.component.scss']
})
export class SupplyProductComponent implements OnInit {
  loading = false;
  productTypes: any[] = [];
  productItems: any[] = [];

  typeColumns = ['name', 'totalItems', 'totalStock', 'updatedAt', 'actions'];
  itemColumns = ['productType', 'name', 'stock', 'reorderLevel', 'unitLabel', 'updatedAt', 'actions'];

  createTypeName = '';
  createItemName = '';
  createItemProductTypeId: number | null = null;
  createItemStock: number | null = 0;
  createItemReorderLevel: number | null = 0;
  createItemUnitLabel = '';

  constructor(private supplyService: SupplyService) {}

  ngOnInit(): void {
    this.loadProducts();
  }

  loadProducts(): void {
    this.loading = true;
    this.supplyService.getProducts().subscribe({
      next: (res) => {
        this.productTypes = (res || []).map((t: any) => ({
          ...t,
          totalItems: t.items?.length || 0,
          totalStock: (t.items || []).reduce((sum: number, item: any) => sum + Number(item?.currentStock || 0), 0)
        }));
        this.productItems = this.productTypes.reduce((acc: any[], t: any) => {
          const rows = (t.items || []).map((i: any) => ({
            ...i,
            productTypeName: t.name,
            updatedAt: t.updatedAt || t.createdAt
          }));
          return acc.concat(rows);
        }, []);
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load product catalog.', 'error');
      }
    });
  }

  addType(): void {
    const name = (this.createTypeName || '').trim();
    if (!name) return;

    this.supplyService.createProductType({ name }).subscribe({
      next: () => {
        this.createTypeName = '';
        this.loadProducts();
        Swal.fire('Saved', 'Product type created.', 'success');
      },
      error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to create product type.', 'error')
    });
  }

  addItem(): void {
    const name = (this.createItemName || '').trim();
    if (!name || !this.createItemProductTypeId) {
      Swal.fire('Required', 'Select product type and enter item name.', 'info');
      return;
    }

    this.supplyService.createProductItem({
      productTypeId: this.createItemProductTypeId,
      name,
      currentStock: Number(this.createItemStock || 0),
      reorderLevel: Number(this.createItemReorderLevel || 0),
      unitLabel: String(this.createItemUnitLabel || '').trim() || null
    }).subscribe({
      next: () => {
        this.createItemName = '';
        this.createItemStock = 0;
        this.createItemReorderLevel = 0;
        this.createItemUnitLabel = '';
        this.loadProducts();
        Swal.fire('Saved', 'Product item created.', 'success');
      },
      error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to create product item.', 'error')
    });
  }

  async editType(row: any): Promise<void> {
    const result = await Swal.fire({
      title: 'Edit Product Type',
      input: 'text',
      inputValue: row.name || '',
      inputPlaceholder: 'Type name',
      showCancelButton: true,
      confirmButtonText: 'Update',
      preConfirm: (value) => {
        if (!String(value || '').trim()) {
          Swal.showValidationMessage('Type name is required.');
          return false;
        }
        return String(value).trim();
      }
    });
    if (!result.isConfirmed) return;

    this.supplyService.updateProductType(row.id, { name: result.value }).subscribe({
      next: () => {
        this.loadProducts();
        Swal.fire('Updated', 'Product type updated.', 'success');
      },
      error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to update product type.', 'error')
    });
  }

  async deleteType(row: any): Promise<void> {
    const result = await Swal.fire({
      title: 'Delete Product Type?',
      text: `This will remove "${row.name}" and its items.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete'
    });
    if (!result.isConfirmed) return;

    this.supplyService.deleteProductType(row.id).subscribe({
      next: () => {
        this.loadProducts();
        Swal.fire('Deleted', 'Product type removed.', 'success');
      },
      error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to delete product type.', 'error')
    });
  }

  async editItem(row: any): Promise<void> {
    const result = await Swal.fire({
      title: 'Edit Product Item',
      html: `
        <input id="swal-item-name" class="swal2-input" placeholder="Item name" value="${this.escapeHtml(row.name || '')}">
        <input id="swal-item-stock" class="swal2-input" type="number" min="0" step="0.01" placeholder="Current stock" value="${Number(row.currentStock || 0)}">
        <input id="swal-item-reorder" class="swal2-input" type="number" min="0" step="0.01" placeholder="Reorder level" value="${Number(row.reorderLevel || 0)}">
        <input id="swal-item-unit" class="swal2-input" placeholder="Unit label (e.g. bags, cartons, kg)" value="${this.escapeHtml(row.unitLabel || '')}">
      `,
      showCancelButton: true,
      confirmButtonText: 'Update',
      preConfirm: () => {
        const name = (document.getElementById('swal-item-name') as HTMLInputElement)?.value || '';
        const currentStock = Number((document.getElementById('swal-item-stock') as HTMLInputElement)?.value || 0);
        const reorderLevel = Number((document.getElementById('swal-item-reorder') as HTMLInputElement)?.value || 0);
        const unitLabel = (document.getElementById('swal-item-unit') as HTMLInputElement)?.value || '';

        if (!String(name || '').trim()) {
          Swal.showValidationMessage('Item name is required.');
          return false;
        }
        if (currentStock < 0 || reorderLevel < 0) {
          Swal.showValidationMessage('Stock values cannot be negative.');
          return false;
        }

        return {
          name: String(name).trim(),
          currentStock,
          reorderLevel,
          unitLabel: String(unitLabel || '').trim() || null
        };
      }
    });
    if (!result.isConfirmed) return;

    this.supplyService.updateProductItem(row.id, result.value).subscribe({
      next: () => {
        this.loadProducts();
        Swal.fire('Updated', 'Product item updated.', 'success');
      },
      error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to update product item.', 'error')
    });
  }

  async deleteItem(row: any): Promise<void> {
    const result = await Swal.fire({
      title: 'Delete Product Item?',
      text: `This will remove "${row.name}".`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete'
    });
    if (!result.isConfirmed) return;

    this.supplyService.deleteProductItem(row.id).subscribe({
      next: () => {
        this.loadProducts();
        Swal.fire('Deleted', 'Product item removed.', 'success');
      },
      error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to delete product item.', 'error')
    });
  }

  stockStatus(row: any): string {
    const stock = Number(row?.currentStock || 0);
    const reorder = Number(row?.reorderLevel || 0);
    if (stock <= 0) return 'Out';
    if (stock <= reorder) return 'Low';
    return 'Healthy';
  }

  private escapeHtml(value: string): string {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
